import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setAuthToken, setUser } from '../utils/auth';
import { toast } from 'sonner';
import { API } from '../config/api';

const SpectacularLogin = () => {
  const [email, setEmail] = useState('owner@lee.com');
  const [password, setPassword] = useState('LEEOwner2026!');
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setMounted(true), 100);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setAuthToken(response.data.token);
      setUser(response.data.user);
      toast.success('Welcome back.', { description: 'LEE is ready.' });
      navigate('/chat');
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        setLoginError("Can't reach LEE right now — check your connection and try again.");
        toast.error('Connection Error', { description: 'Backend not accessible.' });
      } else if (error.response?.status === 401) {
        setLoginError('Invalid email or password.');
        toast.error('Invalid Credentials');
      } else {
        setLoginError(error.response?.data?.detail || 'Login failed — try again.');
        toast.error('Login Failed', { description: error.response?.data?.detail || 'Try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-6 overflow-hidden relative">
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/[0.03] rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      <div className={`w-full max-w-sm relative z-10 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 mb-6">
            <span className="text-2xl font-bold font-mono text-amber-500">L</span>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">LEE</h1>
          <p className="text-zinc-600 text-sm mt-2 font-mono tracking-wider">EXECUTIVE INTELLIGENCE</p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-600 mb-2.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-amber-500/40 focus:ring-1 focus:ring-amber-500/20 transition-all"
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div>
            <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-600 mb-2.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-amber-500/40 focus:ring-1 focus:ring-amber-500/20 transition-all"
              placeholder="Enter password"
              required
            />
          </div>

          {loginError && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-lg border border-red-500/30 bg-red-500/[0.07] px-4 py-3 text-[13px] text-red-300"
            >
              {loginError}
            </div>
          )}

          <button 
            type="submit" 
            className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-lg py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed mt-2"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Authenticating...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Google sign-in — owner-invited emails only */}
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/[0.07]" />
            <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-white/[0.07]" />
          </div>
          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE URLS OR ADD FALLBACKS — THIS BREAKS AUTH
              const redirectUrl = `${window.location.origin}/chat`;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-white text-sm font-semibold py-3 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <p className="text-[10px] text-zinc-700 text-center mt-2">Private system — by owner invitation only</p>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-[10px] text-zinc-700 font-mono">
            Persistent Intelligence · Invention Pipeline · Wealth Engine
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpectacularLogin;
